import { useState, type FormEvent, type ReactElement } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './auth-context.js';
import {
  AuthScreenShell,
  BrandedEmailPreviewCard,
  FirebaseAuthActionPage,
} from './auth-screens.js';
import { Button, Input } from './components/ui/primitives.js';
import { ADMIN_STRINGS, formatPlainAdminError } from './strings.js';
import { resolveAdminEmailPlaceholder } from './use-workspace-identifiers.js';

export function SignInPage(): ReactElement {
  const {
    session,
    signInWithGoogle,
    sendEmailLink,
    sendPasswordReset,
    signInWithPassword,
    emailLinkSent,
    passwordResetSent,
    googleAuthEnabled,
    error,
  } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const actionMode = searchParams.get('mode') ?? '';
  const actionCode = searchParams.get('oobCode') ?? '';
  if ((actionMode === 'resetPassword' || actionMode === 'verifyEmail') && actionCode.length > 0) {
    return <FirebaseAuthActionPage />;
  }
  if (session !== null) {
    return <Navigate to="/products" replace />;
  }

  const runAuthAction = async (
    action: () => Promise<void>,
    fallbackMessage: string,
  ): Promise<void> => {
    setLocalError(null);
    setSubmitting(true);
    try {
      await action();
    } catch (err: unknown) {
      setLocalError(formatPlainAdminError(err, fallbackMessage));
    } finally {
      setSubmitting(false);
    }
  };

  const handleFormSubmit = (e: FormEvent): Promise<void> | void => {
    e.preventDefault();
    return runAuthAction(
      () => signInWithPassword(email, password),
      ADMIN_STRINGS.errors.signInPasswordFallback,
    );
  };

  return (
    <AuthScreenShell
      title="Sign in to Admin Console"
      subtitle="Sign in with your work email to manage products, QR codes, and domains."
      badgeLabel="Admin Console"
    >
      <div className="space-y-4">
        {googleAuthEnabled && (
          <>
            <Button
              variant="secondary"
              className="w-full"
              data-testid="google-sign-in-button"
              onClick={() =>
                void runAuthAction(
                  () => signInWithGoogle(),
                  ADMIN_STRINGS.errors.signInGoogleFallback,
                )
              }
              disabled={submitting}
            >
              Continue with Google
            </Button>
            <div className="relative py-2 text-center text-[12px] text-fg-muted">
              <span className="bg-surface px-2">or sign in with email</span>
            </div>
          </>
        )}

        <form onSubmit={(e) => void handleFormSubmit(e)} noValidate className="space-y-4">
          <div className="space-y-1.5">
            <label
              htmlFor="admin-email"
              className="block text-[12px] font-medium text-fg-secondary"
            >
              Work Email
            </label>
            <Input
              id="admin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={resolveAdminEmailPlaceholder('admin')}
              required
            />
          </div>
          <div className="space-y-1.5" data-testid="sign-in-password-section">
            <div className="flex items-center justify-between">
              <label
                htmlFor="admin-password"
                className="block text-[12px] font-medium text-fg-secondary"
              >
                Password
              </label>
              <Button
                type="button"
                variant="ghost"
                size="inline"
                onClick={() =>
                  void runAuthAction(
                    () => sendPasswordReset(email),
                    ADMIN_STRINGS.errors.signInPasswordResetFallback,
                  )
                }
                disabled={submitting}
                className="text-[12px] text-brand underline decoration-subtle underline-offset-4 hover:decoration-brand"
              >
                Forgot password?
              </Button>
            </div>
            <Input
              id="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
          </div>
          <div className="grid grid-cols-1 gap-2.5 pt-2 sm:grid-cols-2">
            <Button
              type="button"
              variant="secondary"
              size="default"
              className="w-full"
              onClick={() =>
                void runAuthAction(
                  () => sendEmailLink(email),
                  ADMIN_STRINGS.errors.signInEmailLinkFallback,
                )
              }
              disabled={submitting}
            >
              Send Email Link
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="default"
              className="w-full"
              disabled={submitting}
            >
              Sign in with Password
            </Button>
          </div>
        </form>

        {emailLinkSent && (
          <div className="space-y-2">
            <p className="rounded-control border border-status-valid-border bg-status-valid-bg p-3 text-[12px] text-status-valid">
              Sign-in link sent! Check your inbox for the sign-in link.
            </p>
            <BrandedEmailPreviewCard templateId="EMAIL_SIGNIN" recipientEmail={email} />
          </div>
        )}
        {passwordResetSent && (
          <div className="space-y-2">
            <p className="rounded-control border border-status-valid-border bg-status-valid-bg p-3 text-[12px] text-status-valid">
              Password reset email sent! Check your inbox for reset instructions.
            </p>
            <BrandedEmailPreviewCard templateId="PASSWORD_RESET" recipientEmail={email} />
          </div>
        )}
        {(localError ?? error) !== null && (
          <p
            role="alert"
            className="rounded-control border border-status-error-border bg-status-error-bg p-3 text-[12px] text-status-error"
          >
            {localError ?? error}
          </p>
        )}

        <div className="flex items-center justify-between border-t border-subtle pt-3 text-[12px] text-fg-secondary">
          <Button
            type="button"
            variant="ghost"
            size="inline"
            onClick={() => navigate('/reset-password')}
            className="text-[12px] text-brand underline decoration-subtle underline-offset-4 hover:decoration-brand"
          >
            Reset password page
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="inline"
            onClick={() => navigate('/verify-email')}
            className="text-[12px] text-brand underline decoration-subtle underline-offset-4 hover:decoration-brand"
          >
            Verify email address
          </Button>
        </div>
      </div>
    </AuthScreenShell>
  );
}
